import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useUploadedFontOptions } from "@editor/hooks/useFontFamilyOptions";
import { trpc } from "@editor/utils/trpc";

import { errorToast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@replo/design-system/components/shadcn/core/card";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import twMerge from "@replo/design-system/utils/twMerge";
import { useDropzone } from "react-dropzone";

interface FontCardProps {
  fontName: string;
  styleCount: number;
  isUploading?: boolean;
}

const FontCard: React.FC<FontCardProps> = ({
  fontName,
  styleCount,
  isUploading = false,
}) => {
  return (
    <div className="w-[280px] h-[128px] flex flex-col items-center gap-2">
      <Card className="w-full flex-1 bg-sheet p-2">
        <CardContent className="flex flex-col items-center justify-center h-full gap-1">
          {isUploading ? (
            <div className="flex items-center justify-center">
              <Spinner size={24} variant="secondary" />
            </div>
          ) : (
            <>
              <div
                className="text-foreground"
                style={{
                  fontFamily: `"${fontName}", "Inter", sans-serif`,
                  fontSize: "48px",
                  fontWeight: "normal",
                  lineHeight: "1",
                }}
              >
                Aa
              </div>
              <div className="text-sm text-muted">
                {styleCount} style{styleCount !== 1 ? "s" : ""}
              </div>
            </>
          )}
        </CardContent>
      </Card>
      <div className="text-base font-medium text-foreground">
        {isUploading ? "Uploading fonts..." : fontName}
      </div>
    </div>
  );
};

const FontManager: React.FC = () => {
  const projectId = useCurrentProjectId();
  const { refetch: refetchUploadedFonts, fonts } = useUploadedFontOptions();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const fontFaceDeclarations = fonts
    .filter((font) => font.type === "custom")
    .map((font) => {
      return `
        @font-face {
          font-family: "${font.name}";
          src: url("${font.url}") format("woff");
          font-weight: ${font.weight};
          font-style: ${font.style};
          font-display: swap;
        }
      `;
    })
    .join("\n");

  const uploadFontsMutation = trpc.font.uploadFontsFromFontManager.useMutation({
    onSuccess: async () => {
      await refetchUploadedFonts();
    },
  });

  const handleUploadFonts = () => {
    fileInputRef.current?.click();
  };

  const processFiles = async (files: File[]) => {
    if (!(files && files.length > 0)) {
      return;
    }

    if (!projectId) {
      console.error("No project ID available");
      return;
    }

    const validFiles = [];

    for (const file of files) {
      // Validate file type
      const allowedTypes = [".woff", ".woff2", ".ttf", ".otf"];
      const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;

      if (!allowedTypes.includes(fileExtension)) {
        errorToast(
          "Unsupported file type",
          `Replo does not support uploading ${fileExtension} 
            files as fonts. If this is unexpected, please reach out to support@replo.app.`,
        );
        return;
      }

      const arrayBuffer = await file.arrayBuffer();
      const buffer = new Uint8Array(arrayBuffer);

      validFiles.push({
        fontFile: Array.from(buffer),
        filename: file.name,
      });
    }

    if (validFiles.length > 0) {
      await uploadFontsMutation.mutateAsync({
        fonts: validFiles,
        projectId,
      });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      void processFiles(Array.from(files));
    }
  };

  // Only use dropzone for the drop zone area, not the button
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, fileRejections) => {
      // Handle rejected files
      if (fileRejections.length > 0) {
        fileRejections.forEach((rejection) => {
          const { file, errors } = rejection;
          const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
          errors.forEach((error) => {
            if (error.code === "file-invalid-type") {
              errorToast(
                "Unsupported file type",
                `Replo does not support uploading ${fileExtension} 
                    files as fonts. If this is unexpected, please reach out to support@replo.app.`,
              );
            }
          });
        });
        return;
      }

      // Process accepted files
      void processFiles(acceptedFiles);
    },
    accept: [".woff", ".woff2", ".ttf", ".otf"],
    multiple: true,
    disabled: uploadFontsMutation.isPending,
  });

  const fontFamilies = React.useMemo(() => {
    const fontFamilyToNumStyles = new Map<string, number>();

    fonts.forEach((font) => {
      const fontName = font.name;
      fontFamilyToNumStyles.set(
        fontName,
        (fontFamilyToNumStyles.get(fontName) ?? 0) + 1,
      );
    });

    const existingFamilies = Array.from(fontFamilyToNumStyles.entries()).map(
      ([name, count]) => ({
        name,
        styleCount: count,
        isUploading: false,
      }),
    );

    return existingFamilies;
  }, [fonts]);

  const hasCustomFonts = fontFamilies.length > 0;

  return (
    <div
      className={twMerge(
        "flex w-[616px] flex-col gap-4",
        hasCustomFonts ? "h-[376px]" : "h-[296px]",
      )}
    >
      <style dangerouslySetInnerHTML={{ __html: fontFaceDeclarations }} />
      <Card className="h-full w-full p-5 flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Shop Fonts</CardTitle>
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={handleUploadFonts}
              isLoading={uploadFontsMutation.isPending}
              disabled={uploadFontsMutation.isPending}
            >
              {uploadFontsMutation.isPending ? "Uploading..." : "Upload Fonts"}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".woff,.woff2,.ttf,.otf"
              multiple
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </div>
        </CardHeader>
        <CardContent className="flex-1 min-h-0">
          {hasCustomFonts ? (
            <div className="grid grid-cols-2 gap-3 h-full overflow-y-auto overflow-x-hidden no-scrollbar">
              {uploadFontsMutation.isPending && (
                <FontCard
                  key="uploading"
                  fontName="Uploading..."
                  styleCount={0}
                  isUploading={true}
                />
              )}
              {fontFamilies.map((font) => (
                <FontCard
                  key={font.name}
                  fontName={font.name}
                  styleCount={font.styleCount}
                  isUploading={false}
                />
              ))}
            </div>
          ) : (
            <div
              {...getRootProps()}
              className={twMerge(
                "flex h-[200px] w-full flex-col items-center justify-center rounded-lg border-1 border-dashed border-border bg-sheet py-3 px-2 cursor-pointer transition-colors",
                isDragActive && "bg-primary/10 border-primary",
                uploadFontsMutation.isPending &&
                  "opacity-50 cursor-not-allowed",
              )}
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center gap-3">
                <div className="flex items-center gap-2">
                  <img
                    src="/images/shops/fonts/font-upload-graphic.svg"
                    alt="Font upload graphic"
                    width={124}
                    height={68}
                  />
                </div>
                <div className="text-center text-muted-foreground">
                  <p className="font-sm">
                    <span className="font-semibold">Click to upload</span>{" "}
                    <span className="font-normal">or drag and drop</span>
                  </p>
                  <p className="font-normal">(.woff, .woff2, .ttf, .otf)</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FontManager;
